const textDecoder = new TextDecoder();

/**
 * Reads SSH binary payloads by consuming typed fields from a Uint8Array.
 *
 * Usage:
 *   const r = new SshMessageReader(data);
 *   const msgType = r.readByte();
 *   const payload = r.readString();
 */
export class SshMessageReader {
	private view: DataView;
	private offset = 0;

	constructor(private data: Uint8Array) {
		this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
	}

	/** Number of bytes remaining to read. */
	get remaining(): number {
		return this.data.length - this.offset;
	}

	/** Read a single byte. */
	readByte(): number {
		this.ensureAvailable(1);
		return this.view.getUint8(this.offset++);
	}

	/** Read a boolean (1 byte, non-zero = true). */
	readBoolean(): boolean {
		return this.readByte() !== 0;
	}

	/** Read a uint32 in big-endian. */
	readUint32(): number {
		this.ensureAvailable(4);
		const value = this.view.getUint32(this.offset);
		this.offset += 4;
		return value;
	}

	/** Read a uint64 in big-endian as a bigint. */
	readUint64(): bigint {
		this.ensureAvailable(8);
		const value = this.view.getBigUint64(this.offset);
		this.offset += 8;
		return value;
	}

	/** Read N raw bytes without a length prefix. Returns an owned copy. */
	readRawBytes(length: number): Uint8Array {
		this.ensureAvailable(length);
		const slice = this.data.slice(this.offset, this.offset + length);
		this.offset += length;
		return slice;
	}

	/**
	 * Read an SSH "string" (uint32 length + bytes).
	 * Returns the raw bytes.
	 */
	readString(): Uint8Array {
		const length = this.readUint32();
		return this.readRawBytes(length);
	}

	/**
	 * Read an SSH "string" and decode as UTF-8 text.
	 */
	readStringAsText(): string {
		return textDecoder.decode(this.readString());
	}

	/**
	 * Read an SSH "mpint" (multiple precision integer).
	 * Returns a bigint.
	 */
	readMpint(): bigint {
		const bytes = this.readString();
		if (bytes.length === 0) {
			return 0n;
		}

		const negative = (bytes[0]! & 0x80) !== 0;
		let value = 0n;

		if (negative) {
			// two's complement: invert bits, convert, then negate and subtract 1
			for (const byte of bytes) {
				value = (value << 8n) | BigInt(~byte & 0xff);
			}
			value = -(value + 1n);
		} else {
			for (const byte of bytes) {
				value = (value << 8n) | BigInt(byte);
			}
		}

		return value;
	}

	/**
	 * Read an SSH "name-list" (comma-separated ASCII names, length-prefixed).
	 * Returns an array of strings.
	 */
	readNameList(): string[] {
		const text = this.readStringAsText();
		if (text.length === 0) return [];
		return text.split(',');
	}

	/** Read all remaining bytes. */
	readRemaining(): Uint8Array {
		return this.readRawBytes(this.remaining);
	}

	/** Skip N bytes. */
	skip(length: number): void {
		this.ensureAvailable(length);
		this.offset += length;
	}

	private ensureAvailable(length: number): void {
		if (this.offset + length > this.data.length) {
			throw new Error(
				`SSH message truncated: need ${length} bytes at offset ${this.offset}, but only ${this.remaining} remain`,
			);
		}
	}
}
